import axios from 'axios';
import dayjs from 'dayjs';

import { WIZARD_DATA_API } from 'Constants';
import { appConfigStore } from 'Models/AppConfigStore';
import {
	CargoEntity,
	CargoTypeEntity,
	FreightCategoryEntity,
	FreightHandlingEntity,
	TowOnTypeEntity,
} from 'Models/Entities';
import { PassengerDetail } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';
import { TabSelected } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardTypes';
import { FerryTripDto } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInEntities/FerryTripDto';
import { FerryBookingCreationDto } from 'Models/Dtos/BookingDtos';
import { stringIsEmpty } from 'Util/TypeGuards';
import { buildUrl } from 'Util/FetchUtils';
import { Journey } from 'Global/Journey/Types/Journey';
import { CapacityTagDto } from 'Models/Shared/Capacity/CapacityTagDto';

/**
 * A trip note as the wizard renders it. Text only — the server has already dropped the notes this
 * caller may not read (`TicketTabLoadNotes`), so unlike the schedule there is no `showToCustomers`
 * flag to filter on here, and no staff-only note text in the response.
 */
export interface TicketsTabTripNote {
	id: string;
	content: string;
}

export interface TicketsTabTrip {
	id: string;
	name: string;
	isWaitListed: boolean;
	waitlistFull: boolean;
	isHidden: boolean | null;
	vehicleBookingsClosed: boolean | null;
	passengerBookingsClosed: boolean | null;
	freightBookingsClosed: boolean | null;

	/**
	 * Whether this trip's capacity profile is taken out of walk-on bookings. A standing property of
	 * the profile, unlike `passengerBookingsClosed`, which is an operational state the auto-close job
	 * flips before departure — and which also closes vehicle bookings, where this does not.
	 *
	 * Optional because the single-trip alteration fetch and older cached responses predate it.
	 */
	walkOnBookingsDisabled?: boolean | null;
	startDate: Date;
	tripTimeTakenMinutes: number;

	// Ferry trip Capacity
	passengerSpacesAvailable: number;
	passengerSpacesTotal: number;
	vehicleSpacesUsed: number;
	vehicleSpacesAvailable: number;
	vehicleSpacesTotal: number;
	trailerSpotsAvailable: number;
	trailerSpotsTotal: number;
	weightAvailable: number;
	totalWeight: number;

	capacityTags: CapacityTagDto[];

	/**
	 * Trip notes visible to this caller. Empty for the vast majority of trips. Optional because
	 * the single-trip alteration fetch and older cached responses may predate the field.
	 */
	notes?: TicketsTabTripNote[];

	// Feature: Multi-stop - ticket tab trip dto
	departureDateTime: Date;
	arrivalDateTime: Date;
	startStopId: string;
	endStopId: string;
	departed: boolean;
}

export interface TicketsTabData {
	departureTickets: TicketsTabTrip[];
	returningTickets: TicketsTabTrip[];
}

export interface TicketsTabStateData {
	departureTickets: TicketsTabTrip[] | null;
	returningTickets: TicketsTabTrip[] | null;
}

export interface CargoTabDto {
	/**
	 * This will only have the flat property values, no relationships e.g. MinimumLengthId, not MinimumLength
	 */
	cargoTypes: CargoTypeEntity[];
	towOnTypes: TowOnTypeEntity[];
	previousCargos?: CargoEntity[];
	/**
	 * Cargo types the booking customer has a cargo profile for. When the selected vehicle is one of
	 * these, its characteristics are pre-defined by the profile and applied automatically — the booker
	 * does not edit characteristics in the flow. Does not restrict which cargo types are bookable.
	 */
	profileCargoTypeIds?: string[];
}

export interface CargoTabPricingDto {
	cargoMeasurementPrices: { [key: string]: number };
	towOnMeasurementPrices: { [key: string]: number };
}

export interface FreightTypeDto {
	id: string;
	label: string;
	minimumCubic?: number;
	minimumWeight?: number;
	standardWidth?: number;
	standardHeight?: number;
	standardLength?: number;
	standardDescription: string;
	disabled?: boolean;
	freightCategoryId?: string;
	hasStaticPricing: boolean;
}

export interface FreightTabDto {
	freightTypes: { [key in string]: FreightTypeDto[] };
	freightCategoryTypes: FreightCategoryEntity[];
	freightHandlingTypes: FreightHandlingEntity[];
}

export function isTripCompletelyClosedFromBookings(trip: TicketsTabTrip) {
	const passengerBookingsClosed = trip.passengerBookingsClosed ?? false;

	// const vehicleRequiresPassengers = appConfigStore.vehicleBookingRequiresAdult;
	// const vehicleBookingsClosedBecausePassengerBookingsClosed = passengerBookingsClosed && vehicleRequiresPassengers;

	const vehicleBookingsClosed = !appConfigStore.vehicleEnabled || trip.vehicleBookingsClosed;
	const freightBookingsClosed = !appConfigStore.freightEnabled || trip.freightBookingsClosed;

	return (vehicleBookingsClosed || passengerBookingsClosed) && passengerBookingsClosed && freightBookingsClosed;
}

/**
 * Mirrors `BookingTripGuards.IsClosedForBookingType` on the serverside. Keep the two in step — the
 * serverside one is the enforcement and this one is what the screen shows, so a disagreement means
 * a trip the wizard offers and the submit then rejects.
 *
 * Note the walk-on restriction is deliberately absent from the `vehicle` branch: it takes away the
 * walk-on fare, not the ability to carry passengers, so a vehicle booking with people in it is
 * unaffected.
 */
export function isTripClosedByBookingType(trip: TicketsTabTrip | FerryTripDto, typeOfBooking: TabSelected) {
	const passengerBookingsClosed = trip.passengerBookingsClosed ?? false;

	// The check-in FerryTripDto carries only a ferryId, not the vessel, so the flag is absent there.
	// That is the right answer rather than a gap: check-in is staff-only, and staff may place a
	// walk-on booking on a restricted vessel.
	const walkOnDisabledOnVessel = 'walkOnBookingsDisabled' in trip && trip.walkOnBookingsDisabled === true;

	const vehicleBookingsClosed = !appConfigStore.vehicleEnabled || trip.vehicleBookingsClosed;
	const freightBookingsClosed = !appConfigStore.freightEnabled || trip.freightBookingsClosed;

	/*
		Closed passengers close the vehicle tab only where a vehicle needs one.

		This used to be an unconditional `|| passengerBookingsClosed`, with the rule it was meant to
		have sitting beside it commented out. On a site that lets a car travel without a named adult
		— `minRequiredAdultsInVehicleBooking` is 0 — that shut the vehicle tab over a restriction
		that does not apply to it. The same figure already governs whether passenger CAPACITY can
		sell out a vehicle booking, in `useIsSoldOut`, so the two now agree about when passengers
		matter to a car.
	*/
	const vehicleNeedsPassengers = appConfigStore.minRequiredAdultsInVehicleBooking > 0;

	switch (typeOfBooking) {
		case 'vehicle':
			return vehicleBookingsClosed || (vehicleNeedsPassengers && passengerBookingsClosed);
		case 'freight':
			return freightBookingsClosed;
		default:
			return passengerBookingsClosed || walkOnDisabledOnVessel;
	}
}

export async function GetTicketsTabData(
	signal: AbortSignal,
	departureLocationId: string,
	destinationLocationId: string,
	dateFromNoFormat: Date,
	dateToNoFormat: Date,
	takeDeparture: boolean,
	oneWayAlteration: boolean,
	bookingBeingEditedId: string | null,
	userId?: string,
): Promise<TicketsTabData> {
	// Must be date time unspecified timezone. The tab is fetched a date range at a time
	// (one request per direction) rather than one request per day.
	const dateFrom = `${dayjs(dateFromNoFormat).format('YYYY-MM-DD')}T00:00:00`;
	const dateTo = `${dayjs(dateToNoFormat).format('YYYY-MM-DD')}T00:00:00`;

	const { data } = await axios.get<TicketsTabData>(`${WIZARD_DATA_API}/tickets-tab`, {
		params: {
			departureLocationId,
			destinationLocationId,
			dateFrom,
			dateTo,
			takeDeparture,
			bookingBeingEditedId,
			userId,
		},
		signal,
	});

	const tripsToReturn = takeDeparture ? data.departureTickets : data.returningTickets;
	tripsToReturn.forEach(x => {
		x.startDate = new Date(x.startDate);
		x.departureDateTime = new Date(x.departureDateTime);
		x.arrivalDateTime = new Date(x.arrivalDateTime);
	});
	tripsToReturn.sort((a, b) => (a.startDate > b.startDate ? 1 : -1));

	if (oneWayAlteration) {
		return {
			departureTickets: tripsToReturn,
			returningTickets: [],
		};
	}

	data.departureTickets.forEach(x => {
		x.startDate = new Date(x.startDate);
	});
	data.departureTickets = data.departureTickets.sort((a, b) => (a.startDate > b.startDate ? 1 : -1));

	data.returningTickets.forEach(x => {
		x.startDate = new Date(x.startDate);
		x.departureDateTime = new Date(x.departureDateTime);
		x.arrivalDateTime = new Date(x.arrivalDateTime);
	});
	data.returningTickets = data.returningTickets.sort((a, b) => (a.startDate > b.startDate ? 1 : -1));

	return data;
}

/**
 * When a leg next departs on or after `dateFromNoFormat`, up to a year ahead, or `null` when nothing
 * does. The date only — none of the tickets tab's pricing or capacity — so it is cheap enough to look
 * a year past the rail rather than a few weeks.
 */
export async function GetNextSailingDate(
	signal: AbortSignal,
	departureLocationId: string,
	destinationLocationId: string,
	dateFromNoFormat: Date,
	takeDeparture: boolean,
	vehiclesOnly: boolean,
): Promise<Date | null> {
	// Must be date time unspecified timezone, as the tickets tab sends it.
	const dateFrom = `${dayjs(dateFromNoFormat).format('YYYY-MM-DD')}T00:00:00`;

	const { data } = await axios.get<{ departureDateTime: string | null }>(
		`${WIZARD_DATA_API}/tickets-tab/next-sailing`,
		{
			params: {
				departureLocationId,
				destinationLocationId,
				dateFrom,
				takeDeparture,
				vehiclesOnly,
			},
			signal,
		},
	);

	return data.departureDateTime ? new Date(data.departureDateTime) : null;
}

/**
 * Single-trip variant of {@link GetTicketsTabData} used in alteration mode so the user's
 * existing trip card renders instantly while the multi-day window fetch runs in the
 * background. Hits the sibling `/tickets-tab/single` endpoint which reuses the same pipeline
 * (route lookup + capacity + arrival-time resolution + ticket build) on a single trip id.
 */
export async function GetSingleTripTicketData(
	signal: AbortSignal,
	tripId: string,
	departureLocationId: string,
	destinationLocationId: string,
	takeDeparture: boolean,
	oneWayAlteration: boolean,
	bookingBeingEditedId: string | null,
	userId?: string,
): Promise<TicketsTabData> {
	const { data } = await axios.get<TicketsTabData>(`${WIZARD_DATA_API}/tickets-tab/single`, {
		params: {
			tripId,
			departureLocationId,
			destinationLocationId,
			takeDeparture,
			bookingBeingEditedId,
			userId,
		},
		signal,
	});

	const tripsToReturn = takeDeparture ? data.departureTickets : data.returningTickets;
	tripsToReturn.forEach(x => {
		x.startDate = new Date(x.startDate);
		x.departureDateTime = new Date(x.departureDateTime);
		x.arrivalDateTime = new Date(x.arrivalDateTime);
	});

	if (oneWayAlteration) {
		return {
			departureTickets: tripsToReturn,
			returningTickets: [],
		};
	}

	data.departureTickets.forEach(x => {
		x.startDate = new Date(x.startDate);
	});
	data.returningTickets.forEach(x => {
		x.startDate = new Date(x.startDate);
		x.departureDateTime = new Date(x.departureDateTime);
		x.arrivalDateTime = new Date(x.arrivalDateTime);
	});

	return data;
}

export async function FetchCargoTabData(request: FerryBookingCreationDto): Promise<CargoTabDto | null> {
	try {
		const result = await axios.post(`${WIZARD_DATA_API}/get-vehicle-tab-data`, request);
		return {
			cargoTypes: result.data.cargoTypes.map((x: CargoTypeEntity) => new CargoTypeEntity(x)),
			towOnTypes: result.data.towOnTypes.map((x: TowOnTypeEntity) => new TowOnTypeEntity(x)),
			// cargoMeasurementPrices: result.data.cargoMeasurementPrices,
			// towOnMeasurementPrices: result.data.towOnMeasurementPrices,
			previousCargos: result.data.previousCargos.map((x: CargoEntity) => new CargoEntity(x)),
			profileCargoTypeIds: result.data.profileCargoTypeIds ?? [],
		} as CargoTabDto;
	} catch (e) {
		console.error(e);
		return null;
	}
}

export async function FetchCargoTabPricingData(request: FerryBookingCreationDto): Promise<CargoTabPricingDto | null> {
	try {
		const result = await axios.post(`${WIZARD_DATA_API}/get-vehicle-tab-pricing-data`, request);
		return {
			cargoMeasurementPrices: result.data.cargoMeasurementPrices,
			towOnMeasurementPrices: result.data.towOnMeasurementPrices,
		} as CargoTabPricingDto;
	} catch (e) {
		console.error(e);
		return null;
	}
}

export interface FetchVehiclePricingRequest {
	userId: string;
	journeys: Journey[];
	departingJourney: Journey;
	returningJourney: Journey | null;
	vehicleLengthId: string | null;
	vehicleWeightId: string | null;
	traielrLengthId: string | null;

	// Edit mode
	isEditMode: boolean;
	widthId: string | null;
	bookingId: string | null;
}

interface FetchVehiclePricingResult {
	widthPricing: { [key: string]: number };
	vehicleWidthPricing: { [key: string]: number };
	trailerWidthPricing: { [key: string]: number };
}

export async function FetchVehiclePricingData(
	request: FetchVehiclePricingRequest,
): Promise<FetchVehiclePricingResult | null> {
	try {
		const result = await axios.post<FetchVehiclePricingResult>(
			`${WIZARD_DATA_API}/vehicle-pricing-widths`,
			request,
		);
		return result.data;
	} catch (e) {
		console.error(e);
		return null;
	}
}

export async function FetchPassengerTabData(userId: string): Promise<PassengerDetail[]> {
	try {
		if (stringIsEmpty(userId)) {
			return [];
		}

		const result = await axios.get(buildUrl(`${WIZARD_DATA_API}/get-passenger-tab-data`, { userId }));
		return result.data as PassengerDetail[];
	} catch (e) {
		console.error(e);
		return [];
	}
}

export async function FetchFreightTabData(): Promise<FreightTabDto | null> {
	try {
		const result = await axios.get(`${WIZARD_DATA_API}/get-freight-tab-data`);
		return {
			freightTypes: Object.fromEntries(
				Object.entries(result.data.freightTypes).map(([categoryId, freightTypeList]) => [
					categoryId,
					freightTypeList,
				]),
			) as { [key: string]: FreightTypeDto[] },
			freightCategoryTypes: result.data?.freightCategoryTypes.map(
				(x: FreightCategoryEntity) => new FreightCategoryEntity(x),
			),
			freightHandlingTypes: result.data?.freightHandlingTypes.map(
				(x: FreightHandlingEntity) => new FreightHandlingEntity(x),
			),
		} as FreightTabDto;
	} catch (e) {
		console.error(e);
		return null;
	}
}

export interface AddOnsTabData {
	departureTickets: AddOnCapacityRestrictionDto[];
	returningTickets: AddOnCapacityRestrictionDto[];
}

export interface AddOnCapacityRestrictionDto {
	addOnId: string;
	initialCapacity: number;
	remainingCapacity: number;
	iconName?: string;
	canBeOverbooked?: boolean;
	showCapacity?: boolean;
}

export async function getAddOnCapacityRestrictions(
	departureJourney: Journey,
	destinationJourney: Journey | null,
	bookingBeingEditedId: string | null,
	userId?: string,
): Promise<AddOnsTabData> {
	const { data } = await axios.get<AddOnsTabData>(`${WIZARD_DATA_API}/get-add-on-capacities`, {
		params: {
			departureTripId: departureJourney.tripId,
			departureStartStopId: departureJourney.startStopId,
			departureEndStopId: departureJourney.endStopId,
			destinationTripId: destinationJourney?.tripId,
			destinationStartStopId: destinationJourney?.startStopId,
			destinationEndStopId: destinationJourney?.endStopId,
			bookingBeingEditedId,
			userId,
		},
	});

	return data as AddOnsTabData;
}
